import popsss from 'lib/popsss';

// Enforce some typing between two "number" types.
type TimestampMillis = number & {
  __brand: 'Number of milliseconds since the epoch (1 Jan 1970)';
};
type DurationMillis = number & { __brand: 'Number of milliseconds from now' };

const localStorageKey = 'nextRefreshSessionDelayInMillis';
const lockName = 'refreshSession';
const refreshSessionUrl = '/refresh_session';
const SECONDS = 1000 as DurationMillis;
const smartIdActivityCheckInterval = 300;
const refreshSessionInterval = (smartIdActivityCheckInterval *
  SECONDS) as DurationMillis;

// Timestamp helpers
function getNextTimestamp(): TimestampMillis {
  return (Date.now() + refreshSessionInterval) as TimestampMillis;
}

function timestampToDuration(
  timestamp: TimestampMillis,
  now: TimestampMillis,
): DurationMillis {
  return Math.max(timestamp - now, 0) as DurationMillis;
}

// Accessing Shared Timestamp
function setRefreshTimestamp(timestamp: TimestampMillis) {
  window.localStorage.setItem(localStorageKey, JSON.stringify(timestamp));
}

function getRefreshTimestamp(): TimestampMillis {
  const timestampStr = window.localStorage.getItem(localStorageKey);
  if (timestampStr === null) {
    const timestamp = getNextTimestamp();
    setRefreshTimestamp(timestamp);
    return timestamp;
  }
  return JSON.parse(timestampStr) as TimestampMillis;
}

// Recurring event scheduler
export function initRefreshSessionTimer() {
  const timeToWait = timestampToDuration(
    getRefreshTimestamp(),
    Date.now() as TimestampMillis,
  );
  scheduleNextRefreshSession((timeToWait + 1 * SECONDS) as DurationMillis);
}

function scheduleNextRefreshSession(timeToWait: DurationMillis) {
  const timeoutId = setTimeout(refreshSession, Math.max(timeToWait, 0));
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearTimeout(timeoutId);
    }
  });
}

function refreshSession() {
  navigator.locks.request(
    lockName,
    // ifAvailable means the function always runs, but if the lock is in
    // use lock=undefined is passed to the function.
    { ifAvailable: true },
    refreshSessionAndReschedule,
  );
}

// The session refreshing behaviour
function scheduleNextRefreshSessionAndUpdateSharedTimestamp() {
  const nextTimestamp = getNextTimestamp();
  const millisToWait = timestampToDuration(
    nextTimestamp,
    Date.now() as TimestampMillis,
  );
  setRefreshTimestamp(nextTimestamp);
  scheduleNextRefreshSession(millisToWait);
}

async function refreshSessionAndReschedule(lock: Lock) {
  const refreshTimestamp = getRefreshTimestamp();
  const millisToWait = timestampToDuration(
    refreshTimestamp,
    Date.now() as TimestampMillis,
  );
  const tooEarly = millisToWait > 0;
  if (tooEarly) {
    scheduleNextRefreshSession((millisToWait + 1 * SECONDS) as DurationMillis);
    return;
  }

  if (lock) {
    await popsss
      .getJSON(refreshSessionUrl)
      .then(scheduleNextRefreshSessionAndUpdateSharedTimestamp);
    return;
  }

  // The lock is in use, and time has passed, check back after half the interval.
  scheduleNextRefreshSession((refreshSessionInterval / 2) as DurationMillis);
}
